// NotaPengiriman.jsx
import React, { useState, useEffect, useCallback } from 'react';
import SecureFileImg from '../SecureFileImg'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Alert, Snackbar, Stack, useMediaQuery,
  Card, CardContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem, IconButton, LinearProgress,
  Grid, ImageList, ImageListItem, ImageListItemBar, Divider
} from '@mui/material';
import { 
  Refresh, Add, Visibility, Delete, CloudUpload, CheckCircle, Cancel, 
  Receipt, LocalShipping, Image, Description, Close 
} from '@mui/icons-material';
import { getBantuanForNota, getNotaList, createNota, verifyNota, deleteNota } from '../API/notaPengiriman';

const BASE_URL = process.env.REACT_APP_URL || 'http://localhost:5100';

const statusColor = (s) => {
  if (s === 'Diterima') return 'success';
  if (s === 'Ditolak') return 'error';
  return 'warning';
};

const NotaPengiriman = () => {
  const navigate = useNavigate();
  const userRole = useSelector((s) => s.authAdmin?.user?.role);
  const isAdmin = userRole === 'admin';
  const isSupplier = userRole === 'Supplier Penyedia';
  const isMobile = useMediaQuery('(max-width:800px)');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', sev: 'success' });

  // Dialog create
  const [openCreate, setOpenCreate] = useState(false);
  const [bantuanList, setBantuanList] = useState([]);
  const [bantuanLoading, setBantuanLoading] = useState(false);
  const [form, setForm] = useState({ 
    BantuanId: '', 
    SupplierBarangId: '', 
    nomorNota: '', 
    tanggalKirim: '', 
    catatan: '' 
  });
  
  // File states
  const [fotoNota, setFotoNota] = useState(null);
  const [fotoNotaPreview, setFotoNotaPreview] = useState(null);
  const [fotoPengiriman, setFotoPengiriman] = useState(null);
  const [fotoPengirimanPreview, setFotoPengirimanPreview] = useState(null);
  const [fotoBarang, setFotoBarang] = useState(null);
  const [fotoBarangPreview, setFotoBarangPreview] = useState(null);
  const [fotoBarangLainnya, setFotoBarangLainnya] = useState([]);
  const [fotoBarangLainnyaPreviews, setFotoBarangLainnyaPreviews] = useState([]);
  
  const [saving, setSaving] = useState(false);

  // Dialog preview
  const [previewNota, setPreviewNota] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotaList({ page, limit: 10 });
      // Cek struktur response
      const data = res.data?.data || res.data || [];
      setRows(Array.isArray(data) ? data : []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (e) {
      console.error('Error fetch nota:', e);
      setSnackbar({ open: true, msg: 'Gagal memuat data', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

 const handleOpenCreate = async () => {
  setOpenCreate(true);
  setBantuanLoading(true);
  setForm({ 
    BantuanId: '', 
    SupplierBarangId: '', 
    nomorNota: '', 
    tanggalKirim: new Date().toISOString().slice(0, 10), 
    catatan: '' 
  });
  
  // Reset file states
  setFotoNota(null);
  setFotoNotaPreview(null);
  setFotoPengiriman(null);
  setFotoPengirimanPreview(null);
  setFotoBarang(null);
  setFotoBarangPreview(null);
  setFotoBarangLainnya([]);
  setFotoBarangLainnyaPreviews([]);
  
  try {
    const res = await getBantuanForNota();
    console.log('=== Raw API Response ===');
    console.log('Full response:', res);
    console.log('res.data:', res.data);
    console.log('res.data.data:', res.data?.data);
    
    // Cek berbagai kemungkinan struktur data
    let data = [];
    if (res.data?.data && Array.isArray(res.data.data)) {
      data = res.data.data;
    } else if (res.data && Array.isArray(res.data)) {
      data = res.data;
    } else if (Array.isArray(res)) {
      data = res;
    } else if (res.data?.data?.data && Array.isArray(res.data.data.data)) {
      data = res.data.data.data;
    }
    
    console.log('Parsed data:', data);
    console.log('Data length:', data.length);
    
    if (data.length === 0) {
      console.warn('Tidak ada data bantuan yang ditemukan');
    }
    
    setBantuanList(data);
  } catch (e) {
    console.error('Error fetch bantuan:', e);
    setSnackbar({ 
      open: true, 
      msg: 'Gagal memuat data bantuan: ' + (e.response?.data?.message || e.message), 
      sev: 'error' 
    });
  } finally {
    setBantuanLoading(false);
  }
};

  const handleFileChange = (e, setFile, setPreview) => {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { 
      setSnackbar({ open: true, msg: 'Maksimal 5MB', sev: 'warning' }); 
      return; 
    }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const handleMultipleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 5 * 1024 * 1024);
    
    if (validFiles.length !== files.length) {
      setSnackbar({ open: true, msg: 'Beberapa file melebihi 5MB', sev: 'warning' });
    }
    
    setFotoBarangLainnya(prev => [...prev, ...validFiles]);
    const newPreviews = validFiles.map(f => URL.createObjectURL(f));
    setFotoBarangLainnyaPreviews(prev => [...prev, ...newPreviews]);
  };

  const removeAdditionalPhoto = (index) => {
    setFotoBarangLainnya(prev => prev.filter((_, i) => i !== index));
    setFotoBarangLainnyaPreviews(prev => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSave = async () => {
    if (!form.BantuanId) {
      return setSnackbar({ open: true, msg: 'Pilih data bantuan', sev: 'warning' });
    }
    if (!fotoNota) {
      return setSnackbar({ open: true, msg: 'Foto nota wajib diupload', sev: 'warning' });
    }
    
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('BantuanId', form.BantuanId);
      fd.append('SupplierBarangId', form.SupplierBarangId || '');
      fd.append('nomorNota', form.nomorNota || '');
      fd.append('tanggalKirim', form.tanggalKirim || '');
      fd.append('catatan', form.catatan || '');
      
      // Append files
      if (fotoNota) fd.append('fotoNota', fotoNota);
      if (fotoPengiriman) fd.append('fotoPengiriman', fotoPengiriman);
      if (fotoBarang) fd.append('fotoBarang', fotoBarang);
      
      // Append multiple additional photos
      fotoBarangLainnya.forEach(file => {
        fd.append('fotoBarangLainnya', file);
      });
      
      await createNota(fd);
      setSnackbar({ open: true, msg: 'Nota tersimpan', sev: 'success' });
      setOpenCreate(false);
      fetchData();
    } catch (e) {
      console.error('Save error:', e);
      setSnackbar({ open: true, msg: 'Gagal menyimpan: ' + (e.response?.data?.message || e.message), sev: 'error' });
    } finally { 
      setSaving(false); 
    }
  };

  const handleVerify = async (id, status) => {
    try {
      await verifyNota(id, { status });
      setSnackbar({ open: true, msg: `Nota ${status.toLowerCase()}`, sev: 'success' });
      fetchData();
    } catch (e) {
      setSnackbar({ open: true, msg: 'Gagal validasi', sev: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin hapus nota ini?')) return;
    try {
      await deleteNota(id);
      setSnackbar({ open: true, msg: 'Nota dihapus', sev: 'success' });
      fetchData();
    } catch (e) {
      setSnackbar({ open: true, msg: 'Gagal hapus', sev: 'error' });
    }
  };

  const ImagePreviewDialog = ({ open, image, onClose }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 0, position: 'relative', bgcolor: '#1a1a1a', minHeight: 400 }}>
        <IconButton 
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', zIndex: 1 }}
        >
          <Close />
        </IconButton>
        {image && (
          <img 
            src={image} 
            alt="Preview" 
            style={{ width: '100%', height: 'auto', maxHeight: '80vh', objectFit: 'contain' }} 
          />
        )}
      </DialogContent>
    </Dialog>
  );

  const contentStyle = isMobile ? {} : { marginLeft: '250px', marginRight: '20px' };

  return (
    <div className="main-content" style={contentStyle}>
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            <LocalShipping sx={{ mr: 1, verticalAlign: 'middle' }} />
            Nota Pengiriman
          </Typography>
          {isSupplier && (
            <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenCreate}>
              Upload Nota
            </Button>
          )}
        </Box>

        <Card>
          <CardContent>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                    <TableCell sx={{ fontWeight: 600 }}>No</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Bantuan</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Supplier</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>No. Nota</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Tgl Kirim</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Aksi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 4, color: '#888' }}>Tidak ada data.</TableCell></TableRow>
                  ) : rows.map((row, i) => (
                    <TableRow key={row.id || i} sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                      <TableCell>{(page - 1) * 10 + i + 1}</TableCell>
                      <TableCell>#{row.BantuanId} - {row.Bantuan?.JenisBantuan || '-'}</TableCell>
                      <TableCell>{row.SupplierBarang?.namaSupplier || '-'}</TableCell>
                      <TableCell>{row.nomorNota || '-'}</TableCell>
                      <TableCell>{row.tanggalKirim || '-'}</TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(row.status)} label={row.status || 'Dikirim'} />
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" title="Lihat Detail" onClick={() => setPreviewNota(row)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                        {isAdmin && row.status === 'Dikirim' && (
                          <>
                            <IconButton size="small" title="Terima" color="success" onClick={() => handleVerify(row.id, 'Diterima')}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                            <IconButton size="small" title="Tolak" color="error" onClick={() => handleVerify(row.id, 'Ditolak')}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </>
                        )}
                        {(isSupplier || isAdmin) && row.status === 'Dikirim' && (
                          <IconButton size="small" title="Hapus" color="error" onClick={() => handleDelete(row.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 2 }}>
                <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Prev</Button>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>{page} / {totalPages}</Typography>
                <Button size="small" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* Dialog Create */}
        <Dialog open={openCreate} onClose={() => setOpenCreate(false)} maxWidth="md" fullWidth>
          <DialogTitle>Upload Nota Pengiriman</DialogTitle>
          <DialogContent dividers>
            {bantuanLoading ? (
              <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress /></Box>
            ) : (
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
  <InputLabel>Pilih Data Bantuan</InputLabel>
  <Select 
    value={form.BantuanId} 
    label="Pilih Data Bantuan"
    onChange={(e) => {
      const selectedId = parseInt(e.target.value);
      const sel = bantuanList.find(b => b.BantuanId === selectedId);
      console.log('Selected ID:', selectedId);
      console.log('Selected item:', sel);
      setForm(p => ({ 
        ...p, 
        BantuanId: e.target.value, 
        SupplierBarangId: sel?.SupplierBarangId || '' 
      }));
    }}
  >
    {bantuanLoading ? (
      <MenuItem disabled>Loading...</MenuItem>
    ) : bantuanList.length === 0 ? (
      <MenuItem disabled>Tidak ada data bantuan</MenuItem>
    ) : (
      bantuanList.map((b) => (
        <MenuItem key={b.BantuanId} value={b.BantuanId}>
          #{b.BantuanId} - {b.JenisBantuan || '-'} 
          {b.TFL ? ` (TFL: ${b.TFL})` : ''}
        </MenuItem>
      ))
    )}
  </Select>
  {bantuanList.length > 0 && (
    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
      {bantuanList.length} data bantuan tersedia
    </Typography>
  )}
</FormControl>
                
                <TextField 
                  label="Nomor Nota" 
                  size="small" 
                  fullWidth 
                  value={form.nomorNota}
                  onChange={e => setForm(p => ({ ...p, nomorNota: e.target.value }))} 
                />
                
                <TextField 
                  label="Tanggal Kirim" 
                  type="date" 
                  size="small" 
                  fullWidth 
                  value={form.tanggalKirim}
                  onChange={e => setForm(p => ({ ...p, tanggalKirim: e.target.value }))} 
                />
                
                <TextField 
                  label="Catatan" 
                  size="small" 
                  fullWidth 
                  multiline 
                  rows={2} 
                  value={form.catatan}
                  onChange={e => setForm(p => ({ ...p, catatan: e.target.value }))} 
                />

                <Divider>Dokumentasi</Divider>

                {/* Foto Nota - Required */}
                <Box>
                  <Typography variant="caption" color="error">* Wajib</Typography>
                  <Button 
                    component="label" 
                    variant="outlined" 
                    startIcon={<CloudUpload />} 
                    size="small"
                    fullWidth
                    sx={{ mt: 0.5 }}
                  >
                    {fotoNota ? fotoNota.name : 'Upload Foto Nota (Wajib)'}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setFotoNota, setFotoNotaPreview)} />
                  </Button>
                  {fotoNotaPreview && (
                    <Box sx={{ mt: 1, position: 'relative', display: 'inline-block' }}>
                      <img 
                        src={fotoNotaPreview} 
                        alt="Preview Nota" 
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} 
                        onClick={() => setPreviewImage(fotoNotaPreview)}
                      />
                    </Box>
                  )}
                </Box>

                {/* Foto Pengiriman */}
                <Box>
                  <Button 
                    component="label" 
                    variant="outlined" 
                    startIcon={<CloudUpload />} 
                    size="small"
                    fullWidth
                  >
                    {fotoPengiriman ? fotoPengiriman.name : 'Upload Foto Pengiriman (Opsional)'}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setFotoPengiriman, setFotoPengirimanPreview)} />
                  </Button>
                  {fotoPengirimanPreview && (
                    <Box sx={{ mt: 1, display: 'inline-block' }}>
                      <img 
                        src={fotoPengirimanPreview} 
                        alt="Preview Pengiriman" 
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} 
                        onClick={() => setPreviewImage(fotoPengirimanPreview)}
                      />
                    </Box>
                  )}
                </Box>

                {/* Foto Barang */}
                <Box>
                  <Button 
                    component="label" 
                    variant="outlined" 
                    startIcon={<CloudUpload />} 
                    size="small"
                    fullWidth
                  >
                    {fotoBarang ? fotoBarang.name : 'Upload Foto Barang (Opsional)'}
                    <input type="file" hidden accept="image/*" onChange={(e) => handleFileChange(e, setFotoBarang, setFotoBarangPreview)} />
                  </Button>
                  {fotoBarangPreview && (
                    <Box sx={{ mt: 1, display: 'inline-block' }}>
                      <img 
                        src={fotoBarangPreview} 
                        alt="Preview Barang" 
                        style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} 
                        onClick={() => setPreviewImage(fotoBarangPreview)}
                      />
                    </Box>
                  )}
                </Box>

                {/* Foto Barang Lainnya */}
                <Box>
                  <Button 
                    component="label" 
                    variant="outlined" 
                    startIcon={<CloudUpload />} 
                    size="small"
                    fullWidth
                  >
                    Upload Foto Barang Lainnya (Opsional, max 5)
                    <input type="file" hidden accept="image/*" multiple onChange={handleMultipleFileChange} />
                  </Button>
                  {fotoBarangLainnyaPreviews.length > 0 && (
                    <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fotoBarangLainnyaPreviews.map((preview, index) => (
                        <Box key={index} sx={{ position: 'relative', display: 'inline-block' }}>
                          <img 
                            src={preview} 
                            alt={`Barang ${index + 1}`} 
                            style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 4, cursor: 'pointer' }} 
                            onClick={() => setPreviewImage(preview)}
                          />
                          <IconButton
                            size="small"
                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'white', boxShadow: 1 }}
                            onClick={() => removeAdditionalPhoto(index)}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenCreate(false)}>Batal</Button>
            <Button variant="contained" color="success" disabled={saving} onClick={handleSave}>
              {saving ? 'Menyimpan...' : 'Simpan'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog Preview Detail */}
        <Dialog open={!!previewNota} onClose={() => setPreviewNota(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Detail Nota Pengiriman</Typography>
              <IconButton onClick={() => setPreviewNota(null)}><Close /></IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers>
            {previewNota && (
              <Stack spacing={2}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">ID Nota</Typography>
                    <Typography variant="body2" fontWeight={500}>#{previewNota.id}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Status</Typography>
                    <Chip size="small" color={statusColor(previewNota.status)} label={previewNota.status || 'Dikirim'} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Bantuan</Typography>
                    <Typography variant="body2">#{previewNota.BantuanId} - {previewNota.Bantuan?.JenisBantuan || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Supplier</Typography>
                    <Typography variant="body2">{previewNota.SupplierBarang?.namaSupplier || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">No. Nota</Typography>
                    <Typography variant="body2">{previewNota.nomorNota || '-'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">Tanggal Kirim</Typography>
                    <Typography variant="body2">{previewNota.tanggalKirim || '-'}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Catatan</Typography>
                    <Typography variant="body2">{previewNota.catatan || '-'}</Typography>
                  </Grid>
                </Grid>

                <Divider>Dokumentasi</Divider>

                {/* Foto Nota */}
                {previewNota.fotoNota && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Foto Nota</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <SecureFileImg 
                        src={`${BASE_URL}/files/nota-pengiriman/${previewNota.fotoNota}`} 
                        alt="Nota" 
                        style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => setPreviewImage(`${BASE_URL}/files/nota-pengiriman/${previewNota.fotoNota}`)}
                      />
                    </Box>
                  </Box>
                )}

                {/* Foto Pengiriman */}
                {previewNota.fotoPengiriman && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Foto Pengiriman</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <SecureFileImg 
                        src={`${BASE_URL}/files/nota-pengiriman/${previewNota.fotoPengiriman}`} 
                        alt="Pengiriman" 
                        style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => setPreviewImage(`${BASE_URL}/files/nota-pengiriman/${previewNota.fotoPengiriman}`)}
                      />
                    </Box>
                  </Box>
                )}

                {/* Foto Barang */}
                {previewNota.fotoBarang && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Foto Barang</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <SecureFileImg 
                        src={`${BASE_URL}/files/foto-barang/${previewNota.fotoBarang}`} 
                        alt="Barang" 
                        style={{ width: '100%', maxHeight: 300, objectFit: 'contain', borderRadius: 4, cursor: 'pointer' }}
                        onClick={() => setPreviewImage(`${BASE_URL}/files/foto-barang/${previewNota.fotoBarang}`)}
                      />
                    </Box>
                  </Box>
                )}

                {/* Foto Barang Lainnya */}
                {previewNota.fotoBarangLainnya && (() => {
                  try {
                    const additional = JSON.parse(previewNota.fotoBarangLainnya);
                    if (additional.length > 0) {
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary">Foto Barang Lainnya</Typography>
                          <ImageList cols={3} rowHeight={120} sx={{ mt: 0.5 }}>
                            {additional.map((file, index) => (
                              <ImageListItem key={index}>
                                <SecureFileImg 
                                  src={`${BASE_URL}/files/foto-barang/${file}`} 
                                  alt={`Barang ${index + 1}`}
                                  style={{ height: 120, objectFit: 'cover', cursor: 'pointer' }}
                                  onClick={() => setPreviewImage(`${BASE_URL}/files/foto-barang/${file}`)}
                                />
                              </ImageListItem>
                            ))}
                          </ImageList>
                        </Box>
                      );
                    }
                  } catch (e) { return null; }
                  return null;
                })()}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewNota(null)}>Tutup</Button>
          </DialogActions>
        </Dialog>

        {/* Image Preview Dialog */}
        <ImagePreviewDialog 
          open={!!previewImage} 
          image={previewImage} 
          onClose={() => setPreviewImage(null)} 
        />

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={4000} 
          onClose={() => setSnackbar(p => ({ ...p, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            severity={snackbar.sev} 
            onClose={() => setSnackbar(p => ({ ...p, open: false }))} 
            variant="filled"
          >
            {snackbar.msg}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
};

export default NotaPengiriman;